import {
  AddIconData,
  AspectRatioIconData,
  DownloadIconData,
  HomeIconData,
  KeyboardDoubleArrowLeftIconData,
  KeyboardDoubleArrowRightIconData,
  OpenInNewIconData,
  PrintIconData,
  StarFillIconData,
  StarIconData,
} from "@meiden-connect/material-symbols";
import { Button, Icon, useShowConfirmDialog } from "@meiden-connect/ui";
import {
  normalizeCanvasSizePreset,
  resolveCanvasRect,
  type CanvasSizePreset,
} from "@refinement-canvas/domain";
import { useShowToast } from "../notice/use-show-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { generateCanvasId } from "../../../lib/canvas-id";
import { DEFAULT_CANVAS_TITLE, normalizeCanvasTitle } from "../../../lib/canvas-title";
import {
  openPrintWindowWithImage,
  renderCanvasExportImage,
} from "../../../lib/canvas-pdf-export";
import { renderCanvasDownloadPdfBlob } from "../../../lib/canvas-pdf-snapshot";
import { buildPdfDownloadFileName } from "../../../lib/pdf-download-filename";
import { buildPoBoardEpicUrl } from "../../../lib/po-board-link";
import { buildConnectNoteArticleUrl } from "../../../lib/connect-note-link";
import {
  loadAppMenuCollapsed,
  saveAppMenuCollapsed,
} from "../../../lib/app-menu-preference";
import { useCanvasApi } from "../../../lib/canvas-api";
import { getWebConfig } from "../../../env";
import { navigate } from "../../../router";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";

interface Props {
  readonly canvasId: string;
}

// サイズ変更ダイアログのラベルと、縮小判定用の順位（小 < 中 < 大）。
const CANVAS_SIZE_LABEL: Record<CanvasSizePreset, string> = {
  s: "小（8K）",
  m: "中（10K）",
  l: "大（12K）",
};
const CANVAS_SIZE_RANK: Record<CanvasSizePreset, number> = { s: 0, m: 1, l: 2 };

// 左上のフローティングアプリメニュー。
// TOP / 新規ボタンに加えて、現在のキャンバス名をその場で編集できるようにする。
// canvasId が PO ボードのエピック ID（`epic-{UUIDv4}` 形式）と一致する場合は、
// 紐付くエピックを別タブで開くボタンも併せて表示する。
// ボタンが増えてパネルが横に伸びるため、右端の省略ボタンで「ホーム / Connect Note / 省略解除」
// だけを残した省略表示へ切り替えられる（状態は localStorage に保存する）。
export function AppMenu({ canvasId }: Props): JSX.Element {
  const rawTitle = useCanvasDocStore((s) => s.meta.title);
  // 旧データ「未保存のキャンバス」も含めて表示時には常に正規化する。
  const title = normalizeCanvasTitle(rawTitle);
  const dispatcher = useCanvasOpDispatcherContext();
  const canvasApi = useCanvasApi();
  const queryClient = useQueryClient();
  const [draftTitle, setDraftTitle] = useState(title);
  // PDF 出力ボタンの連打防止用のビジー状態。描画と新規ウィンドウ起動が完了するまで再押下を抑止する。
  const [pdfExportBusy, setPdfExportBusy] = useState(false);
  // PDF ダウンロードボタンの連打防止用のビジー状態。Blob 生成が完了するまで再押下を抑止する。
  const [pdfDownloadBusy, setPdfDownloadBusy] = useState(false);
  // お気に入りの登録状態（個人単位）。ブートストラップで seed され、トグルで更新する。
  const isFavorite = useCanvasDocStore((s) => s.meta.isFavorite ?? false);
  const updateLocalMeta = useCanvasDocStore((s) => s.updateLocalMeta);
  // このメニュー上での Ctrl+ホイールもキャンバスズームに振り替える（ブラウザ拡大を防ぐ）。
  const rootRef = useRef<HTMLDivElement | null>(null);
  useCtrlWheelZoom(rootRef);
  // パネルの省略表示状態。既定は展開（従来どおり全ボタンを表示）で、前回の選択を localStorage から復元する。
  const [collapsed, setCollapsed] = useState<boolean>(() => loadAppMenuCollapsed());
  // 省略ボタンと、その開閉対象領域を aria-controls で結び付けるための ID。
  const collapsibleId = useId();
  // お気に入りトグルの多重実行を防ぐビジー状態。
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const showToast = useShowToast();
  const showConfirmDialog = useShowConfirmDialog();
  // 現在のキャンバスサイズプリセット（未設定は既定=中）。サイズ変更ダイアログの現在値と、
  // 印刷 / PDF の出力矩形（resolveCanvasRect）に使う。
  const sizePreset = normalizeCanvasSizePreset(useCanvasDocStore((s) => s.meta.sizePreset));
  const canvasRect = useMemo(() => resolveCanvasRect(sizePreset), [sizePreset]);
  // サイズ変更ダイアログの多重表示を防ぐビジー状態。
  const [sizeBusy, setSizeBusy] = useState(false);
  // PO ボードのエピック詳細を別タブで開くための URL。
  // 環境変数 `VITE_PO_BOARD_ORIGIN` が設定されていればそれを優先し、
  // 未設定の場合はランタイム設定の serviceOrigin（あるいは現在のホスト）の
  // `refinement.` を `po-board.` に置換して導出する。
  // CloudFront 既定ドメイン等で window.location.origin が正規 serviceOrigin と
  // 一致しない経路でもボタンを正しく表示するため、ランタイム設定を優先する。
  const poBoardEpicUrl = useMemo(() => {
    const config = getWebConfig();
    const baseOrigin =
      config.serviceOrigin ??
      (typeof window !== "undefined" ? window.location.origin : undefined);
    return buildPoBoardEpicUrl(
      canvasId,
      baseOrigin,
      import.meta.env.VITE_PO_BOARD_ORIGIN as string | undefined,
    );
  }, [canvasId]);
  // 同じキャンバス ID の Connect Note 記事。記事が未登録の場合も Connect Note 側で
  // 既存キャンバス追加画面へ誘導できるよう、専用クエリ付き URL を構築する。
  const connectNoteArticleUrl = useMemo(() => {
    const config = getWebConfig();
    const baseOrigin =
      config.serviceOrigin ??
      (typeof window !== "undefined" ? window.location.origin : undefined);
    return buildConnectNoteArticleUrl(canvasId, baseOrigin);
  }, [canvasId]);

  useEffect(() => {
    setDraftTitle(title);
  }, [title]);

  // パネルの省略表示 / 展開を切り替え、次回以降も同じ状態で開けるよう保存する。
  const toggleCollapsed = (): void => {
    const next = !collapsed;
    setCollapsed(next);
    saveAppMenuCollapsed(next);
  };

  const commitTitle = (): void => {
    const trimmed = draftTitle.trim();
    const next = trimmed.length > 0 ? trimmed : DEFAULT_CANVAS_TITLE;
    setDraftTitle(next);
    if (next !== rawTitle) {
      dispatcher.updateCanvasMeta({ title: next });
    }
  };

  // 現在のキャンバスを高解像度 PNG に描画したうえで新規ウィンドウに表示し、印刷ダイアログを開く。
  // 連打防止のため exportBusy 中は再実行できない。
  const exportPdf = async (): Promise<void> => {
    if (pdfExportBusy) return;
    // 画像生成（最大数秒）を待ってから window.open() すると user activation が切れ、
    // ポップアップブロックで null になりやすい。クリック直後の同期処理で先に空ウィンドウを開き、
    // 開けなかった場合はその場でトースト通知する（無通知失敗を防ぐ）。
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      showToast(
        "warning",
        "PDF 出力用のウィンドウを開けませんでした。ブラウザのポップアップ設定をご確認ください。"
      );
      return;
    }
    setPdfExportBusy(true);
    try {
      const objects = Object.values(useCanvasDocStore.getState().objects);
      const dataUrl = await renderCanvasExportImage(objects, canvasRect);
      if (!dataUrl) {
        printWindow.close();
        showToast("warning", "PDF 出力用の画像生成に失敗しました。再度お試しください。");
        return;
      }
      // 既に開いている空ウィンドウへ HTML を書き込む。
      openPrintWindowWithImage(printWindow, dataUrl, `${title} - PDF`);
    } catch (error) {
      printWindow.close();
      console.warn("[pdf-export] PDF 出力に失敗", error);
      showToast("warning", "PDF 出力に失敗しました。再度お試しください。");
    } finally {
      setPdfExportBusy(false);
    }
  };

  // 現在のキャンバスを既存の PDF 化機能（クライアント生成）でファイルとしてダウンロードする。
  // 印刷ボタンとは異なり、ブラウザの印刷ダイアログを経由せず直接 PDF ファイルを保存する。
  // 連打防止のため pdfDownloadBusy 中は再実行できない。
  const downloadPdf = async (): Promise<void> => {
    if (pdfDownloadBusy) return;
    setPdfDownloadBusy(true);
    try {
      // 印刷ボタンと同じく、表示中の最新状態（楽観 state・本人の blob プレビュー含む）を出力する。
      const objects = Object.values(useCanvasDocStore.getState().objects);
      const blob = await renderCanvasDownloadPdfBlob(objects, canvasRect);
      if (!blob) {
        showToast("warning", "PDF の生成に失敗しました。再度お試しください。");
        return;
      }
      // Blob を一時 URL 化し、<a download> をプログラム的にクリックして保存させる。
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = buildPdfDownloadFileName(title);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // 一部ブラウザでは click 直後に revoke するとダウンロードが失敗するため、少し遅延して解放する。
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      console.warn("[pdf-export] PDF ダウンロードに失敗", error);
      showToast("warning", "PDF のダウンロードに失敗しました。再度お試しください。");
    } finally {
      setPdfDownloadBusy(false);
    }
  };

  // お気に入りの登録 / 解除をトグルする。楽観更新し、失敗時は元の状態へ戻して通知する。
  const toggleFavorite = async (): Promise<void> => {
    if (favoriteBusy) return;
    const next = !isFavorite;
    setFavoriteBusy(true);
    updateLocalMeta({ isFavorite: next });
    try {
      if (next) {
        await canvasApi.addFavorite(canvasId);
      } else {
        await canvasApi.removeFavorite(canvasId);
      }
      // ダッシュボードの「お気に入り」セクションが最新になるよう再取得を促す
      // （初回追加でセクションが出ない / 最後の 1 件解除でセクションが消えない、を防ぐ）。
      void queryClient.invalidateQueries({ queryKey: ["favorites"] });
    } catch (error) {
      // 失敗したら表示を元に戻す。
      updateLocalMeta({ isFavorite: !next });
      console.warn("[favorite] お気に入りの更新に失敗", error);
      showToast("warning", "お気に入りの更新に失敗しました。再度お試しください。");
    } finally {
      setFavoriteBusy(false);
    }
  };

  // キャンバスサイズ（小 / 中 / 大）を選択して変更する。中心基準で拡大・縮小し、縮小時は
  // 「はみ出た図形はキャンバス外に残る」旨を確認してから適用する。多重表示は sizeBusy で防ぐ。
  const changeCanvasSize = async (): Promise<void> => {
    if (sizeBusy) return;
    setSizeBusy(true);
    try {
      const choice = await showConfirmDialog(
        "info",
        "キャンバスサイズを選択してください（中心を基準に拡大・縮小します）。",
        // 現在のサイズを塗り（action）＋「（現在）」表記で選択中と分かるようにし、他は輪郭（intent）にする。
        // 「キャンセル」ボタンは置かない（右上の ✕ で閉じられ、現在と同じサイズを選んでも下で早期 return
        // するため実質キャンセルと同じ挙動になる）。
        (["s", "m", "l"] as const).map((preset) => ({
          content: preset === sizePreset ? `${CANVAS_SIZE_LABEL[preset]}（現在）` : CANVAS_SIZE_LABEL[preset],
          value: preset,
          variant: preset === sizePreset ? "action" : "intent",
        })),
        { ariaLabel: "キャンバスサイズの選択" }
      );
      // ✕ で閉じた（CloseButtonClick）ときや、現在と同じサイズを選んだときは何もしない（＝キャンセル）。
      if (choice !== "s" && choice !== "m" && choice !== "l") return;
      if (choice === sizePreset) return;
      // 縮小（大→中/小・中→小）はキャンバス範囲が狭まり図形がはみ出す可能性があるため確認する。
      if (CANVAS_SIZE_RANK[choice] < CANVAS_SIZE_RANK[sizePreset]) {
        const confirmed = await showConfirmDialog(
          "warning",
          "キャンバスを縮小すると、範囲外に出た図形はキャンバスの外に残ります（削除はされません）。縮小しますか？",
          [
            { content: "縮小する", value: "ok" },
            { content: "キャンセル", value: "cancel" },
          ],
          { ariaLabel: "キャンバス縮小の確認" }
        );
        if (confirmed !== "ok") return;
      }
      dispatcher.updateCanvasMeta({ sizePreset: choice });
    } finally {
      setSizeBusy(false);
    }
  };

  // 省略ボタンのラベル。省略時は「元の大きさに戻す」操作であることが分かる文言にする。
  const collapseToggleLabel = collapsed ? "メニューを元の大きさに戻す" : "メニューを省略表示にする";

  return (
    <div ref={rootRef} className="canvas-app-menu">
      <Button
        type="button"
        variant="action"
        className="canvas-icon-button"
        onClick={() => navigate("/")}
        aria-label="ダッシュボードへ戻る"
        title="ダッシュボードへ戻る"
      >
        <Icon d={HomeIconData} />
      </Button>
      {/* 省略表示中は、ホーム / Connect Note / 省略解除ボタン以外をまとめて非表示にする。
          省略ボタンから aria-controls で参照できるよう、開閉対象は省略中も残る ID 付きの
          コンテナで囲む（display: contents なので横並びレイアウトには影響しない）。 */}
      <div className="canvas-app-menu-collapsible" id={collapsibleId}>
        {!collapsed ? (
          <>
            <Button
              type="button"
              variant="action"
              className="canvas-icon-button"
              // 新規キャンバスは現在のキャンバスと比較・参照しながら使えるよう別ウィンドウで開く。
              onClick={() => window.open(`/c/${generateCanvasId()}`, "_blank", "noopener,noreferrer")}
              aria-label="新規キャンバスを別ウィンドウで作成"
              title="新規キャンバスを別ウィンドウで作成"
            >
              <Icon d={AddIconData} />
            </Button>
            {poBoardEpicUrl !== null ? (
              <Button
                type="button"
                variant="action"
                className="canvas-icon-button"
                // PO ボード側のエピック詳細を別タブで開く。
                onClick={() =>
                  window.open(poBoardEpicUrl, "_blank", "noopener,noreferrer")
                }
                aria-label="PO ボードのエピックを開く"
                title="PO ボードのエピックを開く"
              >
                <Icon d={OpenInNewIconData} />
              </Button>
            ) : null}
            <label className="canvas-title-editor">
              <span>キャンバス名</span>
              <input
                aria-label="キャンバス名"
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.currentTarget.value)}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.currentTarget.blur();
                  }
                }}
              />
            </label>
            <Button
              type="button"
              variant="action"
              className="canvas-icon-button"
              // キャンバスサイズ（小 / 中 / 大）の変更。中心基準で拡大・縮小する。
              disabled={sizeBusy}
              aria-label={`キャンバスサイズを変更（現在: ${CANVAS_SIZE_LABEL[sizePreset]}）`}
              title={`キャンバスサイズを変更（現在: ${CANVAS_SIZE_LABEL[sizePreset]}）`}
              onClick={() => {
                void changeCanvasSize();
              }}
            >
              <Icon d={AspectRatioIconData} />
            </Button>
            <Button
              type="button"
              variant="action"
              className="canvas-icon-button"
              // お気に入りの登録 / 解除。登録時は塗り星、未登録は枠線星で状態を示す。
              disabled={favoriteBusy}
              aria-pressed={isFavorite}
              aria-label={isFavorite ? "お気に入りを解除" : "お気に入りに登録"}
              title={isFavorite ? "お気に入りを解除" : "お気に入りに登録"}
              onClick={() => {
                void toggleFavorite();
              }}
            >
              <Icon d={isFavorite ? StarFillIconData : StarIconData} />
            </Button>
            <Button
              type="button"
              // クリック直後に印刷せず印刷ダイアログ（PDF 保存含む）を開く準備操作なので intent を使う。
              variant="intent"
              className="canvas-icon-button"
              // 連打防止のため、出力完了までボタンを無効化しビジー表示にする。
              // 本ボタンは印刷ダイアログを開く（PDF 保存も含む）汎用の印刷導線として扱う。
              disabled={pdfExportBusy}
              aria-busy={pdfExportBusy}
              aria-label={pdfExportBusy ? "印刷準備中" : "キャンバスを印刷 / PDF として出力"}
              title={pdfExportBusy ? "印刷準備中" : "キャンバスを印刷 / PDF として出力"}
              onClick={() => {
                void exportPdf();
              }}
            >
              <Icon d={PrintIconData} />
            </Button>
            <Button
              type="button"
              // クリック直後に PDF 生成・保存を即時実行するボタンなので action を使う
              // （印刷ボタンはダイアログを開く準備操作のため intent。ダウンロードは即時実行）。
              variant="action"
              className="canvas-icon-button"
              // 連打防止のため、生成完了までボタンを無効化しビジー表示にする。
              // 既存の PDF 化機能でキャンバスを PDF ファイルとして直接ダウンロードする。
              disabled={pdfDownloadBusy}
              aria-busy={pdfDownloadBusy}
              aria-label={pdfDownloadBusy ? "PDF 生成中" : "キャンバスを PDF としてダウンロード"}
              title={pdfDownloadBusy ? "PDF 生成中" : "キャンバスを PDF としてダウンロード"}
              onClick={() => {
                void downloadPdf();
              }}
            >
              <Icon d={DownloadIconData} />
            </Button>
          </>
        ) : null}
      </div>
      {connectNoteArticleUrl !== null ? (
        <Button
          type="button"
          variant="action"
          className="canvas-icon-button connect-note-icon-button"
          // Connect Note の同じキャンバス ID の記事を別タブで開く。
          onClick={() =>
            window.open(
              connectNoteArticleUrl,
              "_blank",
              "noopener,noreferrer"
            )
          }
          aria-label="Connect Note で開く"
          title="Connect Note で開く"
        >
          <img
            alt=""
            aria-hidden="true"
            className="connect-note-button-icon"
            src="/connect-note-icon.png"
          />
        </Button>
      ) : null}
      {/* 省略ボタンはキャンバス操作ではなくパネル自体の表示切替なので、縦罫線で他のボタンと区切る。 */}
      <span aria-hidden="true" className="canvas-app-menu-divider" />
      <Button
        type="button"
        // クリック直後に表示が切り替わる即時操作なので action を使う。
        variant="action"
        className="canvas-icon-button"
        aria-expanded={!collapsed}
        aria-controls={collapsibleId}
        aria-label={collapseToggleLabel}
        title={collapseToggleLabel}
        onClick={toggleCollapsed}
      >
        <Icon
          d={collapsed ? KeyboardDoubleArrowRightIconData : KeyboardDoubleArrowLeftIconData}
        />
      </Button>
    </div>
  );
}
